import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../assets/images";
import Footer from "../../components/footer";
import Header from "../../components/header";
import CustomModal from "../../components/modal";
import { useTranslate } from "../../context/TranslationContext";
import { useUpdateCourseMutation } from "../../redux/Apis/Course";
import {
  useGetCourseExpiryNotificationsQuery,
  useGetCourseNotificationsQuery,
  useGetCreatorNotificationsQuery,
  useGetLearnerNotificationsQuery,
  useGetTemplateUpdateNotificationsQuery,
  useMarkCreatorNotificationReadMutation,
  useMarkLearnerNotificationReadMutation,
  useUpdateNotificationStatusMutation,
} from "../../redux/Apis/Notification";
import {
  getAccountNotifications,
  isAccountNotificationEvent,
  normalizeCreatorNotification,
  normalizeLearnerNotification,
} from "../../utils/accountNotifications";

const Notifications = () => {
  const { t } = useTranslate("notifications");
  const { t: basketText } = useTranslate("cart");
  const lang = useSelector((state) => state.language.lang);
  const navigate = useNavigate();
  const { isLoggedIn, user } = useSelector((state) => state.authSlice);
  const [modalConfig, setModalConfig] = useState({
    show: false,
    type: null, // "extend" | "archive" | "success"
    item: null,
  });
  const [selectedMonths, setSelectedMonths] = useState("6");
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const {
    data: learnerData,
    isLoading: learnerLoading,
    isFetching: learnerFetching,
    isError: learnerError,
  } = useGetLearnerNotificationsQuery(
    { page: 1, rowsPerPage, lang },
    {
      skip: !isLoggedIn || user?.role === "creator",
      refetchOnMountOrArgChange: true,
    },
  );
  const [markLearnerNotificationRead, { isLoading: markingLearnerRead }] =
    useMarkLearnerNotificationReadMutation();
  const {
    data: creatorData,
    isLoading: creatorLoading,
    isFetching: creatorFetching,
    isError: creatorError,
  } = useGetCreatorNotificationsQuery(
    { page: 1, rowsPerPage, lang },
    {
      skip: !isLoggedIn || !["creator", "both"].includes(user?.role),
      refetchOnMountOrArgChange: true,
    },
  );
  const [markCreatorNotificationRead, { isLoading: markingCreatorRead }] =
    useMarkCreatorNotificationReadMutation();

  const {
    data: courseData,
    isLoading: courseLoading,
    isError: courseError,
    refetch: refetchCourse,
  } = useGetCourseNotificationsQuery(
    { params: { lang } },
    { skip: !isLoggedIn },
  );
  const {
    data: expiryData,
    refetch: refetchExpiry,
    isLoading: expiryLoading,
    isError: expiryError,
  } = useGetCourseExpiryNotificationsQuery(
    { params: { lang } },
    { skip: !isLoggedIn },
  );
  const {
    data: templateUpdateData,
    refetch: refetchTemplateUpdates,
    isLoading: templateLoading,
    isError: templateError,
  } = useGetTemplateUpdateNotificationsQuery(
    { params: { lang } },
    { skip: !isLoggedIn },
  );

  const [updateCourse] = useUpdateCourseMutation();
  const [updateNotificationStatus] = useUpdateNotificationStatusMutation();
  const [expandedId, setExpandedId] = useState(null);

  const openExtendModal = (item) => {
    setSelectedMonths("6");
    setModalConfig({ show: true, type: "extend", item });
  };

  const openArchiveModal = (item) => {
    setModalConfig({ show: true, type: "archive", item });
  };

  const closeModal = () => {
    setModalConfig({ show: false, type: null, item: null });
  };

  useEffect(() => {
    if (isLoggedIn) {
      refetchExpiry();
      refetchCourse();
      refetchTemplateUpdates();
    }
  }, [isLoggedIn, refetchExpiry, refetchCourse, refetchTemplateUpdates]);

  useEffect(() => {
    setRowsPerPage(20);
  }, [lang, user?._id]);

  const expiryNotifications = (expiryData?.data || []).map((item) => ({
    ...item,
    isCampaign: false,
    isExpiry: true,
    title: item.title,
    message: item.message,
    expiry_date: item.expiry_date,
  }));

  const courseNotifications = (courseData?.data || []).map((item) => ({
    ...item,
    source: "course-status",
    isCampaign: false,
    title: item.title,
    message: item.message,
    viewed: item.read,
  }));

  const templateUpdateNotifications = (templateUpdateData?.data || [])
    .filter((item) => item.status === "pending")
    .map((item) => ({
      ...item,
      _id: item.notification_id,
      isCampaign: false,
      isExpiry: false,
      isTemplateUpdate: true,
      title: item.user_course?.title || t("courseUpdateFallback"),
      message: item.message,
      user_course_id: item.user_course?._id,
      template_course_id: item.template_course?._id,
    }));

  const allNotifications = getAccountNotifications(
    [
      ...(learnerData?.data || []).map(normalizeLearnerNotification),
      ...(creatorData?.data || []).map(normalizeCreatorNotification),
      ...templateUpdateNotifications.map((item) => ({
        ...item,
        _uniqueId: `template-${item._id}`,
      })),
      ...expiryNotifications.map((item) => ({
        ...item,
        _uniqueId: `expiry-${item._id}`,
      })),
      ...courseNotifications.map((item) => ({
        ...item,
        _uniqueId: `course-${item._id}`,
      })),
    ],
    user?.role,
  );

  const handleTitleClick = async (item) => {
    setExpandedId((prev) => (prev === item._uniqueId ? null : item._uniqueId));

    if (item.source === "learner" || item.source === "creator") {
      if (!item.read) {
        try {
          const markRead =
            item.source === "creator"
              ? markCreatorNotificationRead
              : markLearnerNotificationRead;
          const request = { id: item._id, lang };
          if (item.source === "creator")
            request.payload = { read: true, action_status: "accepted" };
          await markRead(request).unwrap();
        } catch (error) {
          toast.error(
            error?.data?.message || basketText("notificationReadError"),
          );
        }
      }
      return;
    }

    if (
      !item.isCampaign &&
      !item.isExpiry &&
      !item.isTemplateUpdate &&
      !item.isReportedPost &&
      !item.viewed
    ) {
      if (isAccountNotificationEvent(item)) {
        await updateNotificationStatus({
          id: item._id,
          body: { read: true },
        }).unwrap();
      } else {
        await updateCourse({
          payload: { _id: item._id, read: true },
          params: { lang },
        }).unwrap();
      }
      refetchCourse();
    }

    if (item.isTemplateUpdate && !item.viewed) {
      try {
        await updateNotificationStatus({
          id: item._id,
          body: { read: true },
        });
        refetchTemplateUpdates();
      } catch (err) {
        console.error("Template update notification read error:", err);
      }
    }
  };

  const handleModalAction = async () => {
    setLoading(true);
    try {
      if (modalConfig.type === "extend") {
        const months = parseInt(selectedMonths, 10);
        const newDate = new Date(modalConfig.item.expiry_date);
        newDate.setMonth(newDate.getMonth() + months);
        const payload = {
          _id: modalConfig.item._id,
          expiry_date: newDate.toISOString().split("T")[0],
          expiry_action: true,
        };
        await updateCourse({
          payload,
          params: { lang },
        });
      }

      if (modalConfig.type === "archive") {
        const payload = {
          _id: modalConfig.item._id,
          status: "archived",
          expiry_action: true,
        };
        await updateCourse({
          payload,
          params: { lang },
        });
      }

      setModalConfig({
        show: true,
        type: "success",
        item: null,
      });
      refetchExpiry();
      refetchCourse();
    } catch (err) {
      console.error("Modal action error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRepublish = (item) => {
    navigate(`/course/details/${item._id}`, {
      state: {
        republish: true,
        courseId: item._id,
        activeTab: "v-pills-pricing",
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>Notifications | Traineego</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <Header />
      <div className="banner back-transparent">
        <div className="container py-4">
          <h2 className="mb-4">{t("title")}</h2>

          {courseLoading ||
          expiryLoading ||
          templateLoading ||
          learnerLoading ||
          creatorLoading ? (
            <Spinner />
          ) : courseError ||
            expiryError ||
            templateError ||
            learnerError ||
            creatorError ? (
            <p role="alert">{basketText("notificationError")}</p>
          ) : allNotifications.length === 0 ? (
            <div className="text-center">
              <img
                src={images.modal.no_notification}
                alt={t("noNotifications")}
                className="mb-3 w-50 h-50"
              />
              {/* <p>{t("noNotifications")}</p> */}
            </div>
          ) : (
            allNotifications.map((item) => (
              <div
                key={item._uniqueId}
                className="rounded p-3 mb-3 shadow-sm"
                style={{ backgroundColor: "#0e0e18" }}
              >
                <div className="d-flex justify-content-between align-items-start text-start">
                  {item.image && (
                    <img
                      src={item.image}
                      alt=""
                      width="48"
                      height="48"
                      className="object-fit-cover rounded flex-shrink-0 me-3"
                    />
                  )}
                  <div className="flex-grow-1">
                    {user?.role === "both" && (
                      <span className="badge bg-secondary mb-2">
                        {basketText(`${item.activity}Label`)}
                      </span>
                    )}
                    <h5 className="mb-2 purple-text text-start d-flex align-items-center justify-content-between">
                      <button
                        type="button"
                        className="btn p-0 text-start text-reset fw-semibold"
                        aria-expanded={expandedId === item._uniqueId}
                        disabled={
                          (item.source === "learner" && markingLearnerRead) ||
                          (item.source === "creator" && markingCreatorRead)
                        }
                        onClick={() => handleTitleClick(item)}
                      >
                        {item.course_name?.trim() ||
                          (item.titleKey
                            ? basketText(item.titleKey)
                            : item.title || item.message)}
                      </button>

                      {/* 👉 Notification Type Icons */}
                      {item.isExpiry && (
                        <i
                          className="fa-solid fa-hourglass-half ms-2 text-warning"
                          title={t("expiryNotification")}
                        ></i>
                      )}
                      {item.isTemplateUpdate && (
                        <i
                          className="fa-solid fa-arrows-rotate ms-2 text-success"
                          title={t("templateUpdateAvailable")}
                        ></i>
                      )}
                      {!item.isCampaign &&
                        !item.isExpiry &&
                        !item.isTemplateUpdate &&
                        !item.isReportedPost && (
                          <i
                            className="fa-solid fa-bell ms-2 text-info"
                            title={t("courseNotification")}
                          ></i>
                        )}
                    </h5>
                    {(item.source === "learner" ||
                      item.source === "creator") && (
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                        {item.createdAt && (
                          <time
                            className="p-xs l-grey-text"
                            dateTime={item.createdAt}
                          >
                            {item.date && item.time
                              ? `${item.date} ${item.time}`
                              : new Date(item.createdAt).toLocaleString(lang)}
                          </time>
                        )}
                        {!item.read && (
                          <span className="badge bg-primary">
                            {basketText("unread")}
                          </span>
                        )}
                      </div>
                    )}

                    {expandedId === item._uniqueId && (
                      <>
                        <p className="mb-2">{item.message}</p>

                        {item.cta && item.ctaLabel && (
                          <a
                            href={item.cta}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <button className="site-btn border-btn">
                              {item.ctaLabel}
                            </button>
                          </a>
                        )}

                        {item.expire && (
                          <p className="badge ms-3 text-end float-end flex-grow-1 mb-0 mt-2">
                            {t("expiresOn")} {item.expire}
                          </p>
                        )}
                      </>
                    )}
                    {expandedId === item._uniqueId && item.isExpiry && (
                      <div className="mt-2">
                        <button
                          className="site-btn me-2 mt-2"
                          onClick={() => openExtendModal(item)}
                        >
                          {t("extendButton")}
                        </button>
                        <button
                          className="site-btn border-btn me-2 mt-2"
                          onClick={() => openArchiveModal(item)}
                        >
                          {t("archiveButton")}
                        </button>
                        <button
                          className="site-btn border-btn mt-2"
                          onClick={() => handleRepublish(item)}
                        >
                          {t("editRepublishButton")}
                        </button>
                      </div>
                    )}
                    {expandedId === item._uniqueId && item.isTemplateUpdate && (
                      <div className="mt-2">
                        <button
                          className="site-btn me-2 mt-2"
                          onClick={() =>
                            navigate(`/course/details/${item.user_course_id}`, {
                              state: {
                                templateUpdateReview: true,
                                notificationId: item._id,
                                templateCourseId: item.template_course_id,
                              },
                            })
                          }
                        >
                          {t("reviewUpdate")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          {((!learnerError &&
            (learnerData?.data?.length || 0) < (learnerData?.total || 0)) ||
            (!creatorError &&
              (creatorData?.data?.length || 0) <
                (creatorData?.total || 0))) && (
            <button
              type="button"
              className="site-btn mt-3"
              disabled={learnerFetching || creatorFetching}
              onClick={() => setRowsPerPage((current) => current + 20)}
            >
              {learnerFetching || creatorFetching ? (
                <Spinner size="sm" />
              ) : (
                basketText("loadMoreNotifications")
              )}
            </button>
          )}
        </div>
      </div>

      <CustomModal
        show={modalConfig.show}
        close={closeModal}
        success={modalConfig.type === "success"}
        heading={
          modalConfig.type === "extend"
            ? t("extendModalTitle")
            : modalConfig.type === "archive"
              ? t("archiveModalTitle")
              : t("successModalTitle")
        }
        action={handleModalAction}
        loading={loading}
      >
        {modalConfig.type === "extend" && (
          <div className="mt-3">
            <select
              className="site-input w-170 mb-2 border border-1"
              value={selectedMonths}
              onChange={(e) => setSelectedMonths(e.target.value)}
            >
              <option value="6">{t("extend6")}</option>
              <option value="12">{t("extend12")}</option>
            </select>
          </div>
        )}
      </CustomModal>

      <Footer />
    </>
  );
};

export default Notifications;
